import type { ApiCursorPaginatedResponse, ApiSuccessResponse } from "./http";
import type { Device } from "./device";

/*###########
### ENUMS ###
###########*/
export type UserLang = "pl";
export enum UserRole {
    ADMIN = "admin",
    ACCOUNTANT = "accountant"
}
export enum UserGroup {
    ROOT = "g:root"
}
export enum UserStatus {
    ACTIVE = "active",
    SUSPENDED = "suspended"
}
export enum UserTwoFactorMethod {
    NONE = "none",
    AUTHENTICATOR = "authenticator",
    EMAIL = "email",
    SMS = "sms"
}

/*###########################
### LABELS & SELECT ITEMS ###
###########################*/
export const UserRoleLabel: Record<string, string> = {
    [UserRole.ADMIN]: "Administrator",
    [UserRole.ACCOUNTANT]: "Księgowy"
} as const;

export const UserRoleSelectItems = Object.entries(UserRoleLabel).map(it => ({
    title: it[1],
    value: it[0]
}));

export const UserRoleColor: Record<string, string> = {
    [UserRole.ADMIN]: "purple",
    [UserRole.ACCOUNTANT]: "blue"
} as const;

export const UserStatusLabel: Record<string, string> = {
    [UserStatus.ACTIVE]: "Aktywny",
    [UserStatus.SUSPENDED]: "Zawieszony"
} as const;

export const UserStatusSelectItems = Object.entries(UserStatusLabel).map(it => ({
    title: it[1],
    value: it[0]
}));

export const UserStatusColor: Record<string, string> = {
    [UserStatus.ACTIVE]: "green",
    [UserStatus.SUSPENDED]: "grey"
} as const;

export const UserGroupLabel: Record<string, string> = {
    [UserGroup.ROOT]: "Root"
} as const;

export const UserGroupSelectItems = Object.entries(UserGroupLabel).map(it => ({
    title: it[1],
    value: it[0]
}));

export const UserLangLabel: Record<string, string> = {
    pl: "polski"
} as const;

export const UserLangSelectItems = Object.entries(UserLangLabel).map(it => ({
    title: it[1],
    value: it[0]
}));

/*#################
### PERMISSIONS ###
#################*/
export const USER_PERMISSION = {
    USERS_READ: "users.read",
    USERS_CREATE: "users.create",
    USERS_UPDATE: "users.update",
    USERS_DELETE: "users.delete",
    INVOICING_PLATFORM_CONNECTORS_READ: "invoicing_platform_connectors.read",
    INVOICING_PLATFORM_CONNECTORS_CREATE: "invoicing_platform_connectors.create",
    INVOICING_PLATFORM_CONNECTORS_UPDATE: "invoicing_platform_connectors.update",
    INVOICING_PLATFORM_CONNECTORS_DELETE: "invoicing_platform_connectors.delete",
    API_CLIENTS_READ: "api_clients.read",
    API_CLIENTS_CREATE: "api_clients.create",
    API_CLIENTS_UPDATE: "api_clients.update",
    API_CLIENTS_DELETE: "api_clients.delete",
    API_CLIENT_KEYS_READ: "api_client_keys.read",
    API_CLIENT_KEYS_CREATE: "api_client_keys.create",
    API_CLIENT_KEYS_UPDATE: "api_client_keys.update",
    API_CLIENT_KEYS_DELETE: "api_client_keys.delete",
    INVOICE_INTENTS_READ: "invoice_intents.read",
    INVOICE_INTENTS_CREATE: "invoice_intents.create",
    INVOICE_INTENTS_UPDATE: "invoice_intents.update",
    INVOICE_INTENTS_DELETE: "invoice_intents.delete"
} as const;

export type UserPermission = (typeof USER_PERMISSION)[keyof typeof USER_PERMISSION];

/*################
### MAIN TYPES ###
################*/
export type UserProfileImage = {
    url_xs: string;
    url_sm: string;
    url_md: string;
    url_lg: string;
    url_xl: string;
};
export type UserSettings = {
    lang: UserLang;
    use_detected_timezone: boolean;
    detected_timezone: string;
    selected_timezone: string;
};

export type User = {
    id: `u_${string}`;
    password_set: boolean;
    two_factor_method: UserTwoFactorMethod;
    email: string;
    first_name: string;
    last_name: string;
    phone: string | null;
    profile_image: UserProfileImage | null;
    role: UserRole;
    settings: UserSettings;
    groups: UserGroup[];
    status: UserStatus;
    deleted: boolean;
    created_by: UserPublicFragment | null;
    created_at: string;
    updated_at: string;
};

export type UserDevice = {
    id: `ud_${string}`;
    push_subscription: string | null;
    device: Device;
};

export type UserPublicFragment = Pick<
    User,
    "id" | "first_name" | "last_name" | "profile_image" | "deleted"
>;

/*###################
### HTTP REQUESTS ###
###################*/
export type ApiAuthenticatedUserResponse = ApiSuccessResponse & {
    user: User;
    user_device: UserDevice;
    user_permissions: UserPermission[];
};
export type ApiUserResponse = ApiSuccessResponse & {
    user: User;
};
export type ApiUsersResponse = ApiCursorPaginatedResponse & {
    users: User[];
};
